import React from 'react';
import { Icon } from './Icon';

interface PageShellProps {
    /** Reused for API parity with ModalShell. When false, the shell renders nothing. */
    isOpen: boolean;
    /** Called by the back button in the sticky header. Typically `() => navigate(-1)`. */
    onClose: () => void;
    /**
     * Page title — accepts either a string (rendered in the uppercase bold style)
     * or a ReactNode for rich content such as `<InlineEditableTitle>` or mixed markup.
     * When a ReactNode is passed, the uppercase / two-line clamp styling is NOT applied;
     * the caller controls its own look.
     */
    title: string | React.ReactNode;
    subtitle?: string;
    titleInlineStart?: React.ReactNode;
    titleInlineEnd?: React.ReactNode;
    /**
     * Optional breadcrumb rendered between the back button and the title:
     *   ←   {parent} ›  {title}   {actions}
     * Parent label acts as a subtle clickable link.
     */
    breadcrumb?: { label: string; onClick?: () => void };
    headerMeta?: React.ReactNode;
    /** Tailwind max-width class for the header + content container (e.g. `max-w-5xl`). */
    maxWidth?: string;
    zIndex?: string; // ignored — kept for API parity with ModalShell
    /** Ignored — kept for API parity with ModalShell. A page's unsaved-changes
     *  warning is handled globally by `<UnsavedChangesGuard>` (armed via
     *  `useDirtyTracker`), not by the shell. */
    dirty?: boolean;
    headerActions?: React.ReactNode;
    children: React.ReactNode;
    /** aria-label for the back button. Default Spanish ("Volver"). */
    backAriaLabel?: string;
}

/**
 * Full-page counterpart to `ModalShell`. Sticky header on top, normal page
 * content below. Used for editor pages like `DesignDetailPage`.
 *
 * Two title modes:
 *
 *   • String title — uppercase bold default, wraps to two lines at most:
 *     ```tsx
 *     <PageShell title="MI DISEÑO" ... />
 *     ```
 *
 *   • ReactNode title — caller controls styling, great for inline edit or
 *     multi-part titles. Pair with `breadcrumb` and `InlineEditableTitle`:
 *     ```tsx
 *     <PageShell
 *       breadcrumb={{ label: 'Diseños', onClick: () => navigate('/admin/designs') }}
 *       title={<InlineEditableTitle value={title} onChange={setTitle} />}
 *       headerActions={<><Button>Save</Button><OverflowMenu items={...} /></>}
 *     />
 *     ```
 */
const PageShell: React.FC<PageShellProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    titleInlineStart,
    titleInlineEnd,
    breadcrumb,
    headerMeta,
    maxWidth = 'max-w-5xl',
    headerActions,
    children,
    backAriaLabel = 'Volver',
}) => {
    if (!isOpen) return null;
    const isReactNodeTitle = typeof title !== 'string';

    return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark">
            {/* From md up the sidebar sits beside this bar: `md:min-h-topbar` is
                the height of its brand box, so the two bottom borders meet as
                one line. A bar that wraps onto two rows still grows. */}
            <header data-tour-banner className="sticky top-0 z-30 bg-white dark:bg-surface-dark border-b border-border-subtle dark:border-border-subtle-dark md:min-h-topbar md:flex md:flex-col md:justify-center">
                <div className={`flex flex-wrap items-center gap-2 px-3 py-2 md:px-6 md:py-3 w-full ${maxWidth} mx-auto`}>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        aria-label={backAriaLabel}
                    >
                        <Icon name="arrow_back" className="!text-[22px] text-slate-500 dark:text-slate-400" />
                    </button>

                    {/* A real basis (12rem), not `flex-1`'s 0 — as in ModalShell: with a
                        basis of 0 the bar never wrapped, so the title shrank to
                        whatever the actions left and was cut («LIEFERANT BEARBEI…»,
                        «EDITA…») with room to spare on the line below. Now the
                        actions drop to their own line before the title gets
                        narrower than that. */}
                    <div className="flex-[1_1_12rem] min-w-0 flex items-center gap-1.5 min-h-[28px]">
                        {breadcrumb && (
                            <>
                                <button
                                    type="button"
                                    onClick={breadcrumb.onClick}
                                    disabled={!breadcrumb.onClick}
                                    className="text-sm text-slate-500 dark:text-slate-400 hover:text-primary disabled:hover:text-slate-500 transition-colors truncate shrink-0"
                                >
                                    {breadcrumb.label}
                                </button>
                                <Icon name="chevron_right" className="!text-[16px] text-slate-400 shrink-0" />
                            </>
                        )}
                        {isReactNodeTitle ? (
                            // Rich title — caller owns styling
                            <span className="min-w-0 flex-1 flex items-center gap-1">
                                {titleInlineStart != null ? (
                                    <span className="shrink-0 inline-flex items-center leading-none">{titleInlineStart}</span>
                                ) : null}
                                {title}
                                {titleInlineEnd != null ? (
                                    <span className="shrink-0 inline-flex items-center leading-none">{titleInlineEnd}</span>
                                ) : null}
                            </span>
                        ) : (
                            <div className="min-w-0 flex-1">
                                <h1 className="inline-flex max-w-full min-w-0 items-center gap-1 text-sm md:text-base font-bold text-slate-800 dark:text-white uppercase leading-tight">
                                    {titleInlineStart != null ? (
                                        <span className="shrink-0 inline-flex items-center leading-none">{titleInlineStart}</span>
                                    ) : null}
                                    {/* Two lines, then «…»: a long translated title
                                        («AUSGABE #153 BEARBEITEN») is read whole
                                        instead of cut after one word. */}
                                    <span className="min-w-0 flex-1 break-words hyphens-auto line-clamp-2" title={typeof title === 'string' ? title : undefined}>{title}</span>
                                    {titleInlineEnd != null ? (
                                        <span className="shrink-0 inline-flex items-center leading-none">{titleInlineEnd}</span>
                                    ) : null}
                                </h1>
                                {subtitle && <p className="text-[10px] md:text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{subtitle}</p>}
                            </div>
                        )}
                    </div>

                    {headerMeta != null ? (
                        <div className="flex items-center gap-2 shrink-0 max-w-full">{headerMeta}</div>
                    ) : null}
                    {headerActions && (
                        // Right-aligned and wrapping inside the line, never wider
                        // than it: on a phone a TagSelector + «Guardar» + ⋮ used to
                        // run past the screen's edge. Below `sm` it also grows to
                        // the line's width, so a `flex-1` control inside it (the
                        // TagSelector) can take what «Guardar» and ⋮ leave.
                        <div className="flex flex-wrap items-center justify-end gap-2 min-w-0 max-w-full ml-auto grow sm:grow-0">{headerActions}</div>
                    )}
                </div>
            </header>
            <div data-tour-content className={`${maxWidth} mx-auto px-3 md:px-6 py-4`}>{children}</div>
        </div>
    );
};

export default PageShell;
